import html2canvas from 'html2canvas'
import moment from 'moment'
import React, { FC, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiSpacer, EuiText, EuiTitle, EuiToolTip } from '@elastic/eui'

import { AIEditTargetType } from 'api/interfaces/AIEditTypes'
import { AIPlan, AIPlanCampaignBuilder, AIPlanStub } from 'api/interfaces/AIPlan'
import { useGetAIPlanExecutiveSummarySectionQuery, useUpdateAIPlanIntroductionSectionMutation, useUpdateAIPlanChannelUtilizationSectionMutation, useUpdateAIPlanAudienceStrategySectionMutation, useUpdateAIPlanGeographicTargetingSectionMutation, useHideAIPlanExecutiveSummarySectionMutation, useAddAIPlanCustomSectionMutation, useUpdateAIPlanCustomSectionMutation, useDeleteAIPlanCustomSectionMutation, useUpdateAIPlanPageTitleMutation } from 'api/rtkQueryApi/platform/aiPlansApi'
import { RootState } from 'app/rootReducer'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import GanttChart from 'features/planner/components/GanttChart/GanttChart'
import BackgroundContentCard from 'features/planner/pages/MasterCampaignPlanPage/components/BackgroundContentCard'
import { AddCustomSectionButton } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/AddCustomSectionButton'
import CustomizableSection from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/CustomizableSection'
import EditCustomSectionFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditCustomSectionFlyout'
import EditPageTitleFlyout from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditPageTitleFlyout'
import { RenderCustomSections } from 'features/planner/pages/MasterCampaignPlanPage/components/shared/RenderCustomSections'
import { selectGanttChartImageData, setGanttChartImage } from 'features/planner/pages/MasterCampaignPlanPage/pdfViewSlice'
import { downloadPdf } from 'utils/pdfDownloader'

import { selectAIPlanEditMode } from '../aiPlanSlice'
import { getAIPlanBudgetContent, getAIPlanChannelUtilizationContent, getAIPlanDescription, sortAIPlanCampaigns } from '../utils/aiPlanContentUtils'
import AIPlanExecutiveSummaryPdf from './AIPlanExecutiveSummaryPdf'

interface AIPlanExecutiveSummaryProps {
  aiPlan: AIPlan
  stub?: AIPlanStub
  isStubFetching: boolean
  onAllStaffPdfsClick?: () => void
  staffPdfDisabledReason?: string
  onAIEditSection?: (targetType: AIEditTargetType, targetLabel: string, execSummarySections: Record<string, { title: string; content: string }>) => void
  ganttChartRef?: React.RefObject<HTMLDivElement>
}

type SectionType = 'introduction' | 'channelUtilization' | 'audienceStrategy' | 'geographicTargeting' | 'budget'
type CustomSectionPosition = 'aboveCampaignTimeline' | 'aboveChannelUtilization' | 'aboveAudienceAndGeoSummary' | 'aboveBudget' | 'aboveBottom'

const generateCampaignsHash = (campaigns: AIPlanCampaignBuilder[]): string => {
  const campaignData = campaigns.map(c => ({
    id: c.id,
    name: c.name,
    startDate: c.startDate,
    intervalDuration: c.intervalDuration,
    intervalBudget: c.intervalBudget,
    campaignType: c.campaignType
  }))
  return JSON.stringify(campaignData)
}

const AIPlanExecutiveSummary: FC<AIPlanExecutiveSummaryProps> = ({ aiPlan, stub, isStubFetching, onAllStaffPdfsClick, staffPdfDisabledReason, onAIEditSection, ganttChartRef: externalGanttChartRef }) => {
  const dispatch = useDispatch()
  const editMode = useSelector(selectAIPlanEditMode)
  const ganttImageData = useSelector((state: RootState) => selectGanttChartImageData(state, aiPlan.id))
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const internalGanttChartRef = useRef<HTMLDivElement>(null)
  const ganttChartRef = externalGanttChartRef || internalGanttChartRef

  const [editingSectionType, setEditingSectionType] = useState<SectionType | null>(null)
  const [deletingSectionType, setDeletingSectionType] = useState<SectionType | null>(null)
  const [addingCustomSectionPosition, setAddingCustomSectionPosition] = useState<CustomSectionPosition | null>(null)
  const [editingCustomSection, setEditingCustomSection] = useState<{ position: CustomSectionPosition; index: number } | null>(null)
  const [deletingCustomSection, setDeletingCustomSection] = useState<{ position: CustomSectionPosition; index: number } | null>(null)
  const [editingPageTitle, setEditingPageTitle] = useState(false)

  const { data: executiveSummarySection } = useGetAIPlanExecutiveSummarySectionQuery({ aiPlanId: aiPlan.id })

  const [updateIntroduction, { isLoading: isUpdatingIntroduction }] = useUpdateAIPlanIntroductionSectionMutation()
  const [updateChannelUtilization, { isLoading: isUpdatingChannelUtilization }] = useUpdateAIPlanChannelUtilizationSectionMutation()
  const [updateAudienceStrategy, { isLoading: isUpdatingAudienceStrategy }] = useUpdateAIPlanAudienceStrategySectionMutation()
  const [updateGeographicTargeting, { isLoading: isUpdatingGeographicTargeting }] = useUpdateAIPlanGeographicTargetingSectionMutation()
  const [hideSection, { isLoading: isHidingSection }] = useHideAIPlanExecutiveSummarySectionMutation()
  const [addCustomSection, { isLoading: isAddingCustomSection }] = useAddAIPlanCustomSectionMutation()
  const [updateCustomSection, { isLoading: isUpdatingCustomSection }] = useUpdateAIPlanCustomSectionMutation()
  const [deleteCustomSection, { isLoading: isDeletingCustomSection }] = useDeleteAIPlanCustomSectionMutation()
  const [updatePageTitle, { isLoading: isUpdatingPageTitle }] = useUpdateAIPlanPageTitleMutation()

  const campaigns = sortAIPlanCampaigns(stub?.campaignBuilders ?? [])
  const planStartDate = moment(aiPlan.aiUsedStartDate || aiPlan.planStartDate)
  const planDurationMonths = aiPlan.aiUsedDurationInMonths || aiPlan.planDurationInMonths
  const campaignsHash = generateCampaignsHash(campaigns)

  const captureGanttChart = async () => {
    if (ganttChartRef.current) {
      try {
        const canvas = await html2canvas(ganttChartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          allowTaint: true
        })
        const imageData = canvas.toDataURL('image/png')

        dispatch(
          setGanttChartImage({
            planId: aiPlan.id,
            image: imageData,
            campaignsHash
          })
        )

        return imageData
      } catch {
        return undefined
      }
    }
    return undefined
  }

  const handlePdfDownload = async () => {
    try {
      setIsGeneratingPdf(true)

      const ganttImage = ganttImageData?.image ?? (await captureGanttChart())

      const filename = `${aiPlan.name} - Executive Summary.pdf`

      await downloadPdf(<AIPlanExecutiveSummaryPdf aiPlan={aiPlan} campaigns={campaigns} ganttChartImage={ganttImage} executiveSummarySection={executiveSummarySection} />, filename)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const handleSaveSection = async (title: string, content: string, showTitle: boolean) => {
    if (!editingSectionType) return

    const params = { aiPlanId: aiPlan.id, title, content, showTitle }

    switch (editingSectionType) {
      case 'introduction':
        await updateIntroduction(params)
        break
      case 'channelUtilization':
        await updateChannelUtilization(params)
        break
      case 'audienceStrategy':
        await updateAudienceStrategy(params)
        break
      case 'geographicTargeting':
        await updateGeographicTargeting(params)
        break
    }

    setEditingSectionType(null)
  }

  const confirmDeleteSection = async () => {
    if (!deletingSectionType) return

    await hideSection({
      aiPlanId: aiPlan.id,
      sectionType: deletingSectionType
    })

    setDeletingSectionType(null)
  }

  const handleCancelDelete = () => {
    if (!isHidingSection) {
      setDeletingSectionType(null)
    }
  }

  const handleSaveCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (addingCustomSectionPosition) {
      const existingSections = executiveSummarySection?.[addingCustomSectionPosition] || []
      await addCustomSection({
        aiPlanId: aiPlan.id,
        position: addingCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingCustomSectionPosition(null)
    } else if (editingCustomSection) {
      const existingSections = executiveSummarySection?.[editingCustomSection.position] || []
      await updateCustomSection({
        aiPlanId: aiPlan.id,
        position: editingCustomSection.position,
        existingSections,
        sectionIndex: editingCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingCustomSection(null)
    }
  }

  const confirmDeleteCustomSection = async () => {
    if (!deletingCustomSection) return

    const existingSections = executiveSummarySection?.[deletingCustomSection.position] || []
    await deleteCustomSection({
      aiPlanId: aiPlan.id,
      position: deletingCustomSection.position,
      existingSections,
      sectionIndex: deletingCustomSection.index
    })

    setDeletingCustomSection(null)
  }

  const handleCancelDeleteCustomSection = () => {
    if (!isDeletingCustomSection) {
      setDeletingCustomSection(null)
    }
  }

  const handleSavePageTitle = async (newTitle: string) => {
    await updatePageTitle({
      aiPlanId: aiPlan.id,
      pageTitle: newTitle
    })
    setEditingPageTitle(false)
  }

  const isUpdatingAnySection = isUpdatingIntroduction || isUpdatingChannelUtilization || isUpdatingAudienceStrategy || isUpdatingGeographicTargeting || isAddingCustomSection || isUpdatingCustomSection

  const channelUtilizationHidden = executiveSummarySection?.channelUtilization?.hideAll === true
  const audienceVisible = executiveSummarySection?.audienceStrategy?.hideAll !== true
  const geoVisible = executiveSummarySection?.geographicTargeting?.hideAll !== true
  const budgetHidden = executiveSummarySection?.budget?.hideAll === true

  const getDefaultTitle = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'introduction':
        return ''
      case 'channelUtilization':
        return 'Channel Utilization'
      case 'audienceStrategy':
        return 'Audience Strategy'
      case 'geographicTargeting':
        return 'Geographic Targeting'
      case 'budget':
        return 'Budget'
    }
  }

  const getDefaultShowTitle = (sectionType: SectionType): boolean => {
    return sectionType !== 'introduction'
  }

  const getDefaultContent = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'introduction':
        return getAIPlanDescription(aiPlan.companyName).combined
      case 'channelUtilization':
        return getAIPlanChannelUtilizationContent(campaigns) ?? ''
      case 'audienceStrategy':
        return aiPlan.audienceSummary
      case 'geographicTargeting':
        return aiPlan.geoSummary
      case 'budget':
        return getAIPlanBudgetContent(campaigns, false)
    }
  }

  const getSectionDisplayName = (sectionType: SectionType): string => {
    switch (sectionType) {
      case 'introduction':
        return 'Introduction'
      case 'channelUtilization':
        return 'Channel Utilization'
      case 'audienceStrategy':
        return 'Audience Strategy'
      case 'geographicTargeting':
        return 'Geographic Targeting'
      case 'budget':
        return 'Budget'
    }
  }

  const getSectionName = (sectionType: SectionType | null): string => {
    if (!sectionType) return 'Section'

    const section = executiveSummarySection?.[sectionType]
    const title = section?.title?.trim()

    if (title && title.length > 0 && section?.showTitle) {
      return title
    }

    return getSectionDisplayName(sectionType)
  }

  const getDeleteCustomSectionTitle = (): string => {
    if (!deletingCustomSection) return 'Delete Custom Section'

    const sections = executiveSummarySection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Delete ${title}`
    }

    return 'Delete Custom Section'
  }

  const getDeleteCustomSectionMessage = (): string => {
    if (!deletingCustomSection) return 'Are you sure you want to delete this custom section?'

    const sections = executiveSummarySection?.[deletingCustomSection.position] || []
    const section = sections[deletingCustomSection.index]
    const title = section?.title?.trim()

    if (title && title.length > 0) {
      return `Are you sure you want to delete ${title}?`
    }

    return 'Are you sure you want to delete this custom section?'
  }

  const getDisplayedTitle = (sectionType: SectionType): string => {
    const section = executiveSummarySection?.[sectionType]
    return section?.title ?? getDefaultTitle(sectionType)
  }

  const getDisplayedContent = (sectionType: SectionType): string => {
    const section = executiveSummarySection?.[sectionType]
    return section?.content ?? getDefaultContent(sectionType)
  }

  const buildExecSummarySections = (): Record<string, { title: string; content: string }> => ({
    introduction: { title: getDisplayedTitle('introduction'), content: getDisplayedContent('introduction') },
    channelUtilization: { title: getDisplayedTitle('channelUtilization'), content: getDisplayedContent('channelUtilization') },
    audienceStrategy: { title: getDisplayedTitle('audienceStrategy'), content: getDisplayedContent('audienceStrategy') },
    geographicTargeting: { title: getDisplayedTitle('geographicTargeting'), content: getDisplayedContent('geographicTargeting') }
  })

  const pageTitle = executiveSummarySection?.pageTitle || 'Executive Summary'

  return (
    <>
      <EuiFlexGroup justifyContent='spaceBetween'>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiTitle size='m'>
                <h3>{pageTitle}</h3>
              </EuiTitle>
            </EuiFlexItem>
            {editMode && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content='Edit Title'>
                    <EuiButtonIcon iconType='visText' style={{ color: '#0071c2' }} onClick={() => setEditingPageTitle(true)} aria-label='Edit title' size='s' />
                  </EuiToolTip>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup>
            {onAllStaffPdfsClick && (
              <EuiFlexItem grow={false}>
                {staffPdfDisabledReason ? (
                  <EuiToolTip content={staffPdfDisabledReason}>
                    <EuiButton size='s' color='primary' disabled>
                      All Staff PDFs
                    </EuiButton>
                  </EuiToolTip>
                ) : (
                  <EuiButton size='s' color='primary' onClick={onAllStaffPdfsClick}>
                    All Staff PDFs
                  </EuiButton>
                )}
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButton size='s' color='primary' onClick={handlePdfDownload} isLoading={isGeneratingPdf} disabled={isGeneratingPdf}>
                Client PDF
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiHorizontalRule />

      {!executiveSummarySection?.introduction?.hideAll && (
        <>
          <CustomizableSection section={executiveSummarySection?.introduction} defaultTitle='' defaultContent={getAIPlanDescription(aiPlan.companyName).combined} defaultShowTitle={false} separator='spacer' onEdit={() => setEditingSectionType('introduction')} onAIEdit={editMode ? () => onAIEditSection?.('ExecSummaryIntroduction', 'Edit Introduction', buildExecSummarySections()) : undefined} onDelete={() => setDeletingSectionType('introduction')} showActions={editMode} />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={executiveSummarySection?.aboveCampaignTimeline} position='aboveCampaignTimeline' editMode={editMode} onEdit={(position: string, index: number) => setEditingCustomSection({ position: position as CustomSectionPosition, index })} onDelete={(position: string, index: number) => setDeletingCustomSection({ position: position as CustomSectionPosition, index })} />
      {editMode && (
        <>
          <AddCustomSectionButton position='aboveCampaignTimeline' onClick={() => setAddingCustomSectionPosition('aboveCampaignTimeline')} />
          <EuiSpacer size='xl' />
        </>
      )}

      <BackgroundContentCard>
        <EuiFlexGroup justifyContent='center'>
          <EuiFlexItem style={{ textAlign: 'center' }}>
            <EuiTitle size='s'>
              <h4>Campaign Timeline</h4>
            </EuiTitle>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size='m' />
        {isStubFetching ? (
          <EuiText>
            <p>Loading campaign data...</p>
          </EuiText>
        ) : (
          <GanttChart campaigns={campaigns as any} planStartDate={planStartDate} planDurationMonths={planDurationMonths} isClickable={false} isHoverable={false} presorted />
        )}
      </BackgroundContentCard>

      <EuiSpacer size='xl' />

      <div ref={ganttChartRef} style={{ position: 'absolute', left: '-99999px', top: '-99999px', width: '1200px', overflow: 'visible' }}>
        <BackgroundContentCard>
          <EuiFlexGroup justifyContent='center'>
            <EuiFlexItem style={{ textAlign: 'center' }}>
              <EuiTitle size='s'>
                <h4>Campaign Timeline</h4>
              </EuiTitle>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size='m' />
          <GanttChart campaigns={campaigns as any} planStartDate={planStartDate} planDurationMonths={planDurationMonths} ganttChartRef={ganttChartRef} isClickable={false} isHoverable={false} presorted />
        </BackgroundContentCard>
      </div>

      <RenderCustomSections sections={executiveSummarySection?.aboveChannelUtilization} position='aboveChannelUtilization' editMode={editMode} onEdit={(position: string, index: number) => setEditingCustomSection({ position: position as CustomSectionPosition, index })} onDelete={(position: string, index: number) => setDeletingCustomSection({ position: position as CustomSectionPosition, index })} />
      {!channelUtilizationHidden && (
        <>
          {editMode && (
            <>
              <AddCustomSectionButton position='aboveChannelUtilization' onClick={() => setAddingCustomSectionPosition('aboveChannelUtilization')} />
              <EuiSpacer size='xl' />
            </>
          )}
          <CustomizableSection
            section={executiveSummarySection?.channelUtilization}
            defaultTitle='Channel Utilization'
            defaultContent={getAIPlanChannelUtilizationContent(campaigns) ?? ''}
            defaultShowTitle={true}
            separator='spacer'
            onEdit={() => setEditingSectionType('channelUtilization')}
            onAIEdit={editMode ? () => onAIEditSection?.('ExecSummaryChannelUtilization', 'Edit Channel Utilization', buildExecSummarySections()) : undefined}
            onDelete={() => setDeletingSectionType('channelUtilization')}
            showActions={editMode}
          />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={executiveSummarySection?.aboveAudienceAndGeoSummary} position='aboveAudienceAndGeoSummary' editMode={editMode} onEdit={(position: string, index: number) => setEditingCustomSection({ position: position as CustomSectionPosition, index })} onDelete={(position: string, index: number) => setDeletingCustomSection({ position: position as CustomSectionPosition, index })} />
      {(audienceVisible || geoVisible) && editMode && (
        <>
          <AddCustomSectionButton position='aboveAudienceAndGeoSummary' onClick={() => setAddingCustomSectionPosition('aboveAudienceAndGeoSummary')} />
          <EuiSpacer size='xl' />
        </>
      )}

      <EuiFlexGroup direction='row' gutterSize='xl' alignItems='flexStart' wrap>
        {audienceVisible && (
          <EuiFlexItem style={{ flexBasis: audienceVisible && geoVisible ? '500px' : '100%', minHeight: '0' }}>
            <CustomizableSection
              section={executiveSummarySection?.audienceStrategy}
              defaultTitle='Audience Strategy'
              defaultContent={aiPlan.audienceSummary}
              defaultShowTitle={true}
              separator='horizontalRule'
              onEdit={() => setEditingSectionType('audienceStrategy')}
              onAIEdit={editMode ? () => onAIEditSection?.('ExecSummaryAudienceStrategy', 'Edit Audience Strategy', buildExecSummarySections()) : undefined}
              onDelete={() => setDeletingSectionType('audienceStrategy')}
              showActions={editMode}
            />
          </EuiFlexItem>
        )}
        {geoVisible && (
          <EuiFlexItem style={{ flexBasis: audienceVisible && geoVisible ? '500px' : '100%', minHeight: '0' }}>
            <CustomizableSection
              section={executiveSummarySection?.geographicTargeting}
              defaultTitle='Geographic Targeting'
              defaultContent={aiPlan.geoSummary}
              defaultShowTitle={true}
              separator='horizontalRule'
              onEdit={() => setEditingSectionType('geographicTargeting')}
              onAIEdit={editMode ? () => onAIEditSection?.('ExecSummaryGeographicTargeting', 'Edit Geographic Targeting', buildExecSummarySections()) : undefined}
              onDelete={() => setDeletingSectionType('geographicTargeting')}
              showActions={editMode}
            />
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      <EuiSpacer size='xl' />

      <RenderCustomSections sections={executiveSummarySection?.aboveBudget} position='aboveBudget' editMode={editMode} onEdit={(position: string, index: number) => setEditingCustomSection({ position: position as CustomSectionPosition, index })} onDelete={(position: string, index: number) => setDeletingCustomSection({ position: position as CustomSectionPosition, index })} />
      {!budgetHidden && (
        <>
          {editMode && (
            <>
              {(executiveSummarySection?.aboveBudget?.length || 0) > 0 && <EuiSpacer size='m' />}
              <AddCustomSectionButton position='aboveBudget' onClick={() => setAddingCustomSectionPosition('aboveBudget')} />
              <EuiSpacer size='xl' />
            </>
          )}
          {!editMode && (executiveSummarySection?.aboveBudget?.length || 0) > 0 && <EuiSpacer size='m' />}
          <CustomizableSection section={executiveSummarySection?.budget} defaultTitle='Budget' defaultContent={getAIPlanBudgetContent(campaigns, false)} defaultShowTitle={true} separator='spacer' onDelete={() => setDeletingSectionType('budget')} showActions={editMode} />
          <EuiSpacer size='xl' />
        </>
      )}

      <RenderCustomSections sections={executiveSummarySection?.aboveBottom} position='aboveBottom' editMode={editMode} onEdit={(position: string, index: number) => setEditingCustomSection({ position: position as CustomSectionPosition, index })} onDelete={(position: string, index: number) => setDeletingCustomSection({ position: position as CustomSectionPosition, index })} />
      {editMode && <AddCustomSectionButton position='aboveBottom' onClick={() => setAddingCustomSectionPosition('aboveBottom')} />}

      {editingSectionType && <EditCustomSectionFlyout section={executiveSummarySection?.[editingSectionType]} defaultTitle={getDefaultTitle(editingSectionType)} defaultContent={getDefaultContent(editingSectionType)} defaultShowTitle={getDefaultShowTitle(editingSectionType)} flyoutTitle={`Edit ${getSectionName(editingSectionType)}`} onSave={handleSaveSection} onClose={() => setEditingSectionType(null)} isLoading={isUpdatingAnySection} />}

      {(addingCustomSectionPosition || editingCustomSection) && (
        <EditCustomSectionFlyout
          section={editingCustomSection ? executiveSummarySection?.[editingCustomSection.position]?.[editingCustomSection.index] : undefined}
          defaultTitle=''
          defaultContent=''
          defaultShowTitle={true}
          flyoutTitle={addingCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSaveCustomSection}
          onClose={() => {
            setAddingCustomSectionPosition(null)
            setEditingCustomSection(null)
          }}
          isLoading={isUpdatingAnySection}
        />
      )}

      {deletingSectionType && <AcConfirmModal message='Are you sure you want to delete this section?' title={`Delete ${getSectionName(deletingSectionType)}`} onCancel={handleCancelDelete} onConfirm={confirmDeleteSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isHidingSection} confirmButtonDisabled={isHidingSection} />}

      {deletingCustomSection && <AcConfirmModal message={getDeleteCustomSectionMessage()} title={getDeleteCustomSectionTitle()} onCancel={handleCancelDeleteCustomSection} onConfirm={confirmDeleteCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingCustomSection} confirmButtonDisabled={isDeletingCustomSection} />}

      {editingPageTitle && <EditPageTitleFlyout currentTitle={executiveSummarySection?.pageTitle || 'Executive Summary'} defaultTitle='Executive Summary' onSave={handleSavePageTitle} onClose={() => setEditingPageTitle(false)} isLoading={isUpdatingPageTitle} />}
    </>
  )
}

export default AIPlanExecutiveSummary
